/**
 * Persistent storage (issue #3): asked for exactly once, when the first install completes,
 * with the answer kept in the settings for the About screen; the live estimate beside it.
 */
import { getSetting, setSetting } from "../character";

export type PersistResult = "granted" | "denied" | "unsupported";

export interface PersistRecord {
  result: PersistResult;
  at: string;
}

/** Request persistent storage unless it was requested before; returns the record either way. */
export async function requestPersistOnce(now = new Date()): Promise<PersistRecord> {
  const stored = await getSetting("persist").catch(() => null);
  if (stored) return stored;
  let result: PersistResult = "unsupported";
  if (typeof navigator !== "undefined" && navigator.storage && typeof navigator.storage.persist === "function") {
    try {
      result = (await navigator.storage.persist()) ? "granted" : "denied";
    } catch {
      result = "denied";
    }
  }
  const record: PersistRecord = { result, at: now.toISOString() };
  await setSetting("persist", record).catch(() => undefined);
  return record;
}

export interface StorageFacts {
  persisted: boolean | null;
  usage: number | null;
  quota: number | null;
}

/** What the browser says now: whether the origin is persisted, and the estimate. */
export async function storageFacts(): Promise<StorageFacts> {
  const facts: StorageFacts = { persisted: null, usage: null, quota: null };
  if (typeof navigator === "undefined" || !navigator.storage) return facts;
  try {
    if (typeof navigator.storage.persisted === "function") facts.persisted = await navigator.storage.persisted();
  } catch {
    facts.persisted = null;
  }
  try {
    if (typeof navigator.storage.estimate === "function") {
      const e = await navigator.storage.estimate();
      facts.usage = e.usage ?? null;
      facts.quota = e.quota ?? null;
    }
  } catch {
    facts.usage = null;
  }
  return facts;
}
